import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import axios from "axios";
import { showToast } from '../../../components/common/notification/Notification';
import PropertyDetailCard from "./PropertyDetailCard";
import SaaSNotFoundState from "../../../components/common/saas/SaaSNotFoundState";
import SaaSLoadingSkeleton from "../../../components/common/saas/SaaSLoadingSkeleton";

const PropertySingle = ({ backBtn, ownerId: setOwnerId }) => { 
  const { Id } = useParams();
  const [payloadData, setPayloadData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false); 
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL;

  const [mediaData, setMediaData] = useState({
    images: [],
    videos: [],
    attachments: [],
  });

  useEffect(() => {
    fetchPropertyItem();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  const fetchPropertyItem = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${baseUrl}/property/propertyDetail/${Id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.status === 200) {
        const propertyData = response.data.propertyInfo || {};
        if (setOwnerId && propertyData.user) {
          setOwnerId(propertyData.user.id); 
        }
        setMediaData({
          images: (propertyData.images || []).map((img) => ({
            url: assetUrl + img.image_url,
          })),
          videos: (propertyData.videos || []).map((video) => ({
            url: assetUrl + video.url_path,
          })),
          attachments: (propertyData.attachments || []).map((attachment) => ({
            attachment_url: assetUrl + attachment.attachment_url,
          })),
        });

        setPayloadData({ 
            "title": propertyData.name || "",
            "address": propertyData.address || "",
            "land_mark": propertyData.land_mark || "",
            "are": propertyData.are,
            "pin_code": propertyData.pin_code || "",
            "city_name": propertyData.city_name || "",
            "state_name": propertyData.state_name || "",
            "lead_id": propertyData.id || "",
            "country_name": "India",
            "area_name": propertyData.area_name || "",
            "type": propertyData.property_sub_type?.property_type_name || null,
            "purpose": propertyData.purpose?.name || "", 
            "property_status_id": propertyData.property_status_id || null,
            "sub_type": propertyData.property_sub_type?.name || null,
            "sub_sub_type": propertyData.property_sub_sub_type?.name || null,
            "active": propertyData.active || false,
            "structure" : { 
              "id": propertyData.structure?.id || null, 
              "bed_rooms": propertyData.structure?.bed_rooms || 0,
              "bath_rooms": propertyData.structure?.bath_rooms || 0,
              "rooms": propertyData.structure?.rooms || 0,
            },
            "feature": {
              "active": propertyData.feature?.active || false,
              "swimming_pool": propertyData.feature?.swimming_pool || false,
              "gas_connection": propertyData.feature?.gas_connection || false,
              "ro": propertyData.feature?.ro || false,
              "club_house": propertyData.feature?.club_house || false,
              "basketball_court": propertyData.feature?.basketball_court || false,
              "tennis_court": propertyData.feature?.tennis_court || false,
              "gym": propertyData.feature?.gym || false,
              "indoor_games": propertyData.feature?.indoor_games || false,
              "child_play_area": propertyData.feature?.child_play_area || false,
              "hospital_near_by": propertyData.feature?.hospital_near_by || false,
              "mall_near_by": propertyData.feature?.mall_near_by || false,
              "market_near_by": propertyData.feature?.market_near_by || false,
              "school_near_by": propertyData.feature?.school_near_by || false,
            },
            "images": mediaData.images || [],
            "videos": mediaData.videos || [],
            "attachments": mediaData.attachments || [],
            "description": propertyData.description || "", 
            "creater": propertyData.user ? `${propertyData.user.first_name || ''} ${propertyData.user.last_name || ''}`.trim() : "",
            "cost": propertyData.price || "",
            "photo": propertyData.user?.photo ? assetUrl + propertyData.user.photo : null, 
            "createdOn": propertyData.feature?.created_on || propertyData.created_on || "",
            "isPublished": propertyData.is_published || false,
            "user_type": propertyData.user?.userType?.name || "",
            "is_address_added": propertyData.user?.is_address_added || false,
            "is_become_a_partner": propertyData.user?.is_become_a_partner || false,
            "is_email_verified": propertyData.user?.is_email_verified || false,
            "is_phone_verified": propertyData.user?.is_phone_verified || false,
            "is_whatsapp_number": propertyData.user?.is_whatsapp_number || false,
        });
        setIsError(false);
      } else {
        showToast('Failed to fetch property data', { type: 'error' });
      }
    } catch (error) {
      setIsError(true);
      showToast('Error fetching property data', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const cleanBack = backBtn ? backBtn.replace(/\/+$/, '') : '';
  const discussionPath = cleanBack && Id ? `${cleanBack}/${Id}/discussion` : null;

  if (isLoading) {
    return (
      <div className="container-fluid px-3 py-4" style={{ maxWidth: "1200px" }}>
        <SaaSLoadingSkeleton type="card" count={1} />
      </div>
    );
  }

  if (isError || !payloadData.title) {
    return (
      <SaaSNotFoundState
        title="Property not found"
        description="This property may have been removed or is no longer available."
        backLink={backBtn || "/customer/properties"}
        backLabel="Back to Properties"
      />
    );
  }

  return ( 
    <div className="container-fluid px-0 mb-3" style={{ maxWidth: "1200px" }}>
      <PropertyDetailCard
        name={payloadData.title}
        address={payloadData.address}
        land_mark={payloadData.land_mark}
        area={payloadData.are}
        pin_code={payloadData.pin_code}
        propertyType={payloadData.type}
        city={payloadData.city_name} 
        state={payloadData.state_name}
        country={payloadData.country_name}
        structure={payloadData.structure || {}}
        description={payloadData.description || ""}
        feature={payloadData.feature || {}}
        price={payloadData.cost || ""}
        propertyPurpose={payloadData.purpose} 
        property_sub_type={payloadData.sub_type || ""}
        property_sub_sub_type={payloadData.sub_sub_type || ""}
        images={mediaData.images}
        videos={mediaData.videos}  
        attachments={mediaData.attachments}
        creater={payloadData.creater} 
        photo={payloadData.photo}
        createdOn={payloadData.createdOn}
        isPublished={payloadData.isPublished}
        user_type={payloadData.user_type}
        is_address_added={payloadData.is_address_added}
        is_become_a_partner={payloadData.is_become_a_partner}
        is_email_verified={payloadData.is_email_verified}
        is_phone_verified={payloadData.is_phone_verified}
        is_whatsapp_number={payloadData.is_whatsapp_number}
        // eslint-disable-next-line eqeqeq
        isAdmin={loggedInUser?.user?.userType?.name == 'SUPER_ADMIN'} 
        backBtn={backBtn}
        discussionPath={discussionPath}
      />  
    </div>
  );
};

export default PropertySingle;